import { useEffect, useRef } from "react";
import useAgencyIsAppSumo from "../../../hooks/useAgencyIsAppSumo";
import { AUTOMATIC_AGENT_REQUEST_KINDS } from "../../../utils/constants";

export const isCampaignOpeningAssistEligible = ({ campaignId, chatMessages }) =>
  Boolean(campaignId) && Object.keys(chatMessages || {}).length === 0;

/**
 * Auto-sends the campaign opening summary prompt when the embedded chat is empty
 * and the campaign has finished syncing. AppSumo agencies skip this send.
 */
export const useCampaignOpeningAutoPrompt = ({
  campaignId,
  chatMessages,
  isSyncingData,
  chatActionsRef,
  pendingOpeningHighlightRef,
}) => {
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  /** Once per campaign visit; chat reset must not re-trigger the opening prompt. */
  const autoPromptSentForCampaignIdRef = useRef(null);

  const isEligible = isCampaignOpeningAssistEligible({
    campaignId,
    chatMessages,
  });

  if (
    campaignId &&
    autoPromptSentForCampaignIdRef.current !== campaignId &&
    isAppSumoResolved &&
    isAppSumo
  ) {
    autoPromptSentForCampaignIdRef.current = campaignId;
  }

  const canAutoSend =
    isEligible &&
    isAppSumoResolved &&
    !isAppSumo &&
    !isSyncingData &&
    typeof chatActionsRef.current?.triggerHiddenUserTurn === "function";

  const openingAssistConsumed =
    Boolean(campaignId) &&
    (autoPromptSentForCampaignIdRef.current === campaignId ||
      !isAppSumoResolved);

  useEffect(() => {
    if (!canAutoSend) {
      return undefined;
    }

    if (autoPromptSentForCampaignIdRef.current === campaignId) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const triggerHiddenUserTurn =
        chatActionsRef.current?.triggerHiddenUserTurn;
      if (!triggerHiddenUserTurn) {
        return;
      }

      // Passive opening summary: server owns the prompt and does not bill it.
      const accepted = await Promise.resolve(
        triggerHiddenUserTurn(null, {
          automaticKind: AUTOMATIC_AGENT_REQUEST_KINDS.CAMPAIGN_OPENING_SUMMARY,
        })
      );
      if (cancelled || !accepted) {
        return;
      }
      autoPromptSentForCampaignIdRef.current = campaignId;
      if (pendingOpeningHighlightRef) {
        pendingOpeningHighlightRef.current = true;
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chatActionsRef is stable ref object
  }, [canAutoSend, campaignId, chatMessages, isAppSumo, isAppSumoResolved]);

  return { openingAssistConsumed };
};
